import React from "react";
import { Shield, Zap, Target, BarChart3, ChevronRight, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import heroBg from "../assets/hero-bg.png";

const HeroSection = () => {
    const navigate = useNavigate();
    return (
        <div className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-slate-900">
            {/* Background Image with Overlays */}
            <div className="absolute inset-0 z-0">
                <img 
                    src={heroBg} 
                    alt="Proprietary GIS Analytics" 
                    className="w-full h-full object-cover opacity-60 scale-105 animate-slow-zoom"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent z-10" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent z-10" />
            </div>

            <div className="container mx-auto px-6 relative z-20">
                <div className="max-w-4xl">
                    {/* Exclusive Insight Badge */}
                    <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-400/30 backdrop-blur-md text-blue-300 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-8 animate-fade-in-up">
                        <Shield className="w-3.5 h-3.5" />
                        Proprietary California GIS Intelligence
                    </div>

                    {/* Main Headline - High Impact */}
                    <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight animate-fade-in-up animation-delay-200">
                        The Intelligent Edge in 
                        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-sky-300 to-indigo-400">
                            Property Development
                        </span>
                    </h1>

                    {/* Subheadline - Persona Driven */}
                    <p className="text-lg md:text-xl text-slate-300 font-medium leading-relaxed mb-10 max-w-2xl animate-fade-in-up animation-delay-400">
                        Empowering <span className="text-white font-bold">Realtors</span>, <span className="text-white font-bold">Architects</span>, and <span className="text-white font-bold">Developers</span> with the instant feasibility data required to unlock millions in site potential. 
                    </p>

                    {/* Gated CTA Section */}
                    <div className="flex flex-col sm:flex-row gap-4 items-center animate-fade-in-up animation-delay-600">
                        <button 
                            onClick={() => navigate('/dashboard')}
                            className="w-full sm:w-auto px-8 py-5 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-2xl shadow-2xl shadow-blue-500/40 transition-all active:scale-95 flex items-center justify-center gap-3 text-lg group"
                        >
                            <Zap className="w-5 h-5 fill-current" />
                            Analyze Any Property 
                            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                        
                        <div className="flex items-center gap-2 text-slate-400 px-4 py-2 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50">
                            <Lock className="w-4 h-4" />
                            <span className="text-sm font-semibold">Member Access Only</span>
                        </div>
                    </div>

                    {/* Persona Grid - Quick Value Props */}
                    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 animate-fade-in-up animation-delay-800">
                        {[
                            { label: "Realtors", icon: Target, desc: "Sell faster with data" },
                            { label: "Architects", icon: BarChart3, desc: "Instant MFA/MDA" },
                            { label: "Developers", icon: Zap, desc: "Fesibility in seconds" },
                            { label: "Buyers", icon: Shield, desc: "Know before offer" }
                        ].map((p, idx) => (
                            <div key={idx} className="group p-4 rounded-2xl border border-slate-700/30 bg-slate-800/20 backdrop-blur-sm hover:bg-slate-800/40 transition-all cursor-default overflow-hidden relative">
                                <div className="absolute top-0 right-0 w-12 h-12 bg-blue-500/5 rounded-full blur-xl group-hover:bg-blue-500/10 transition-colors" />
                                <p className="text-white font-bold text-sm mb-1">{p.label}</p>
                                <p className="text-slate-400 text-xs leading-tight">{p.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Bottom Glow Effect */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />

            <style>{`
                @keyframes slow-zoom {
                    0% { transform: scale(1.05); }
                    100% { transform: scale(1.15); }
                }
                .animate-slow-zoom {
                    animation: slow-zoom 20s ease-in-out infinite alternate;
                }
                .animate-fade-in-up {
                    opacity: 0;
                    transform: translateY(20px);
                    animation: fadeInUp 0.8s ease-out forwards;
                }
                @keyframes fadeInUp {
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animation-delay-200 { animation-delay: 0.2s; }
                .animation-delay-400 { animation-delay: 0.4s; }
                .animation-delay-600 { animation-delay: 0.6s; }
                .animation-delay-800 { animation-delay: 0.8s; }
            `}</style>
        </div>
    );
};

export default HeroSection;
